import React from 'react';
import { cn } from '~/lib/utils';
import themes from '~/design-system/components/v1_5/Header/HeaderTheme.module.css';
import styles from './PreviewStage.module.css';
import type { HeaderTheme } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

export interface PreviewStageProps {
  /** Centred on both axes. The artifact, or whichever state has taken its place. */
  children?: React.ReactNode;
  /**
   * Everything that floats: the zoom cluster, the bottom line, the generating pill. Rendered
   * outside the scrolling window and after the centred child, so it stacks over the artifact and
   * holds still while the artifact moves — with no z-index anywhere in the file.
   */
  overlays?: React.ReactNode;
  /**
   * A primary press on the mat around the artifact: in the scrolling window but outside the artifact's canvas
   * (the frame, its handles). The floating controls are outside the window and never count. Beka, 2026-09-27:
   * a click there takes the selection off, as it does in Figma.
   */
  onBackgroundPress?: () => void;
  /** Accessible name for the scrolling window, which is a tab stop. */
  label?: string;
  theme?: HeaderTheme;
  className?: string;
}

/**
 * The mat: everything below the artifact bar, edge to edge.
 *
 * One 2% white wash over the pane's own ground and nothing else — no radius, no border, no padding.
 * The lack of padding is the point: the ~37px of air around the artifact is a consequence of the
 * fit, not of a gutter on the stage, which is why zooming past Fit crops against the pane's edges
 * instead of against an inset box.
 *
 * That crop is reached by SCROLLING the inner window. Panning is a gesture and is not built, and
 * `overflow: clip` — what the stage used to carry — creates no scroll container at all, so
 * everything outside the pane at 200% was unreachable by pointer, keyboard and script alike. The
 * scrollbars are suppressed because the boards draw none, and the window is a focusable region so
 * that suppressing them costs nobody the content.
 *
 * The forwarded ref is that window rather than the stage: it is the box the artifact is fitted to
 * AND the box that scrolls, and the two are the same size.
 */
export const PreviewStage = React.forwardRef<HTMLDivElement, PreviewStageProps>(function PreviewStage(
  { children, overlays, onBackgroundPress, label = 'Artifact', theme: themeProp, className },
  ref,
) {
  const theme = useV15Theme(themeProp);
  return (
    <div className={cn(themes[theme], styles.stage, className)}>
      <div
        ref={ref}
        className={styles.viewport}
        // A scroll region the user must be able to reach without a wheel in both axes. `group`
        // rather than `region`: it is one object on a mat, not a landmark worth an entry in a
        // screen reader's region list.
        role="group"
        aria-label={label}
        // The rule reads `group` as purely structural and objects to focusing it. A scrollable box
        // is the case it does not model: WCAG wants scroll containers reachable by keyboard, and
        // past Fit there may be no other route in — an artifact is arbitrary content and is not
        // guaranteed to hold a focusable node, and the scrollbars are suppressed.
        // `ComposerAttachmentTray` and `v1_5/Layout/Seam` disable the same rule for the same reason.
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
        onPointerDown={
          onBackgroundPress
            ? (event) => {
                if (event.button !== 0) return;
                const target = event.target as Element | null;
                if (target?.closest?.('[data-artifact-canvas]')) return;
                onBackgroundPress();
              }
            : undefined
        }
      >
        {children}
      </div>
      {overlays}
    </div>
  );
});

export interface StageIndicatorProps {
  /** Verbatim. `2 of 8`, `PNG · 2.4 MB`, `live · 1440 × 900`, `you can keep editing the others`. */
  children: React.ReactNode;
  theme?: HeaderTheme;
  className?: string;
}

/**
 * The bottom-centre line: one polymorphic sentence about what you are looking at.
 *
 * A label, never a control — the boards are explicit that there are no back/next arrows here,
 * because "arrows tell you nothing about what is next". It sits 2px lower than every floating pill
 * (18 against 16) so that bare text and a bordered box optically share an edge.
 *
 * It lives beside `PreviewStage` rather than in its own file for the same reason `MenuSpacer` lives
 * beside `MenuSurface`: it is one element, it only ever appears on this stage, and it shares this
 * stylesheet.
 */
export function StageIndicator({ children, theme: themeProp, className }: StageIndicatorProps) {
  const theme = useV15Theme(themeProp);
  return <div className={cn(themes[theme], styles.indicator, className)}>{children}</div>;
}
